import React, { useState, useEffect, useCallback } from 'react';
import {
  fetchRuns, fetchRun, createRun, postStageEvent, fetchPacket, fetchStageChecks,
  executeStage,
} from './onboardingApi';

const STAGE_STATE_STYLE = {
  pending: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Pending' },
  blocked: { bg: 'bg-gray-50', text: 'text-gray-400', label: 'Blocked' },
  running: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Running' },
  awaiting_human: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Awaiting human' },
  complete: { bg: 'bg-green-100', text: 'text-green-800', label: 'Complete' },
  failed: { bg: 'bg-red-100', text: 'text-red-800', label: 'Failed' },
};

const StateBadge = ({ state }) => {
  const s = STAGE_STATE_STYLE[state] || STAGE_STATE_STYLE.pending;
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${s.bg} ${s.text}`}>
      {s.label}
    </span>
  );
};

const Chip = ({ children }) => (
  <span className="inline-block px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 text-[10px] font-mono">
    {children}
  </span>
);

// Log lines streamed by the runner into this stage's `log` events, flattened
// in event order for the tail view.
function stageLogLines(run, stageId) {
  const lines = [];
  for (const e of run.events || []) {
    if (e.stage_id === stageId && e.event === 'log') {
      for (const ln of (e.payload && e.payload.lines) || []) lines.push(ln);
    }
  }
  return lines;
}

function StageRow({ run, stage, onEvent, onExecute, onError }) {
  const [packet, setPacket] = useState(null);
  const [checks, setChecks] = useState(null);
  const [showLogs, setShowLogs] = useState(false);
  const [action, setAction] = useState(null);
  const [busy, setBusy] = useState(false);

  const consoleActions = stage.console_actions || [];
  const job = stage.job || null;
  const selectedAction = action || consoleActions[0] || null;

  const act = async (fn) => {
    setBusy(true);
    try { await fn(); } catch (e) { onError(e.message); } finally { setBusy(false); }
  };

  const togglePacket = () => act(async () => {
    if (packet) { setPacket(null); return; }
    const p = await fetchPacket(run.id, stage.id);
    setPacket(p.packet_markdown);
  });

  const runChecks = () => act(async () => {
    const c = await fetchStageChecks(run.id, stage.id);
    setChecks(c);
  });

  const execute = () => act(async () => {
    setShowLogs(true);
    await onExecute(stage.id, selectedAction);
  });

  const event = (name) => act(() => onEvent(stage.id, name));

  const { state } = stage;
  const jobRunning = job && job.state === 'running';
  const logLines = stageLogLines(run, stage.id);
  return (
    <div className="py-2 border-b border-gray-50 last:border-0">
      <div className="flex items-center gap-3 text-sm">
        <span className="w-8 font-mono text-xs text-gray-400">{stage.id}</span>
        <div className="w-32 flex-shrink-0"><StateBadge state={state} /></div>
        <div className="flex-1">
          <span className="text-gray-800">{stage.name}</span>
          <span className="ml-2 space-x-1">
            <Chip>{stage.executor}</Chip>
            <Chip>{stage.location_default}</Chip>
            {stage.paid && <Chip>$</Chip>}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={togglePacket} disabled={busy}
            className="px-2 py-0.5 text-xs bg-gray-100 rounded hover:bg-gray-200">
            Packet
          </button>
          <button onClick={runChecks} disabled={busy}
            className="px-2 py-0.5 text-xs bg-gray-100 rounded hover:bg-gray-200">
            Checks
          </button>
          {logLines.length > 0 && (
            <button onClick={() => setShowLogs((v) => !v)} disabled={busy}
              className="px-2 py-0.5 text-xs bg-gray-100 rounded hover:bg-gray-200">
              {showLogs ? 'Hide logs' : 'Logs'}
            </button>
          )}
          {consoleActions.length > 0 && (
            <>
              {consoleActions.length > 1 && (
                <select value={selectedAction || ''} onChange={(e) => setAction(e.target.value)}
                  disabled={busy || jobRunning}
                  className="px-1 py-0.5 text-xs border border-gray-200 rounded bg-white">
                  {consoleActions.map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
              )}
              <button onClick={execute} disabled={busy || jobRunning}
                title="Run this stage from the console (A1)"
                className="px-2 py-0.5 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50">
                {jobRunning ? 'Running…' : `Execute${consoleActions.length === 1 ? ` (${selectedAction})` : ''}`}
              </button>
            </>
          )}
          {state === 'pending' && (
            <button onClick={() => event('started')} disabled={busy}
              className="px-2 py-0.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700">
              Start
            </button>
          )}
          {state === 'running' && (
            <>
              <button onClick={() => event('human_gate_opened')} disabled={busy}
                className="px-2 py-0.5 text-xs bg-yellow-500 text-white rounded hover:bg-yellow-600">
                Open gate
              </button>
              <button onClick={() => event('completed')} disabled={busy}
                className="px-2 py-0.5 text-xs bg-green-600 text-white rounded hover:bg-green-700">
                Complete
              </button>
              <button onClick={() => event('failed')} disabled={busy}
                className="px-2 py-0.5 text-xs bg-red-600 text-white rounded hover:bg-red-700">
                Fail
              </button>
            </>
          )}
          {state === 'awaiting_human' && (
            <button onClick={() => event('human_gate_resolved')} disabled={busy}
              className="px-2 py-0.5 text-xs bg-yellow-600 text-white rounded hover:bg-yellow-700">
              Resolve gate
            </button>
          )}
        </div>
      </div>
      {job && (
        <div className="ml-11 mt-1 text-xs text-gray-500">
          Console job: <span className="font-mono">{job.action}</span> ·{' '}
          <span className={job.state === 'failed' ? 'text-red-600' : job.state === 'running' ? 'text-blue-600' : 'text-gray-600'}>
            {job.state}
          </span>
          {job.exit_code !== null && job.exit_code !== undefined && ` · exit ${job.exit_code}`}
          {job.error && <span className="text-red-600"> · {job.error}</span>}
        </div>
      )}
      {stage.artifacts.length > 0 && (
        <div className="ml-11 mt-1 text-xs text-gray-500">
          Artifacts: {stage.artifacts.map((a, i) => (
            <span key={i} className="font-mono mr-2">{a.name || a.ref || JSON.stringify(a)}</span>
          ))}
        </div>
      )}
      {showLogs && logLines.length > 0 && (
        <pre className="ml-11 mt-2 text-[11px] leading-tight bg-gray-900 text-gray-100 rounded p-3 overflow-x-auto max-h-64 overflow-y-auto whitespace-pre-wrap">
          {logLines.join('\n')}
        </pre>
      )}
      {checks && (
        <div className="ml-11 mt-1 text-xs space-y-0.5">
          {checks.checks.length === 0 && (
            <span className="text-gray-400">No machine checks for this stage — resolves at the human gate.</span>
          )}
          {checks.checks.map((c) => (
            <div key={c.check} className={c.passed ? 'text-green-700' : 'text-red-700'}>
              {c.passed ? '✓' : '✗'} {c.check}{c.detail ? ` — ${c.detail}` : ''}
            </div>
          ))}
        </div>
      )}
      {packet && (
        <pre className="ml-11 mt-2 text-xs bg-gray-50 rounded p-3 overflow-x-auto whitespace-pre-wrap text-gray-700">
          {packet}
        </pre>
      )}
    </div>
  );
}

// `runId` (optional): pin the panel to one specific run. Without it the panel
// shows the slug's LATEST run — fine for the institution-scoped mount, but the
// all-runs list (PipelineRuns) must pass runId, since a slug can have multiple
// non-active historical runs (migration 084's partial index only constrains
// ACTIVE runs) and "Open" on an old row must not silently show the newest one.
function RunPanel({ slug, runId = null }) {
  const [runs, setRuns] = useState([]);
  const [run, setRun] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (runId) {
        setRuns([{ id: runId }]);
        setRun(await fetchRun(runId));
      } else {
        const rs = await fetchRuns(slug);
        setRuns(rs);
        if (rs.length > 0) setRun(await fetchRun(rs[0].id));
        else setRun(null);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [slug, runId]);

  useEffect(() => { load(); }, [load]);

  const onCreate = async () => {
    if (creating) return;
    setCreating(true);
    setError(null);
    try {
      const r = await createRun(slug);
      setRuns([r, ...runs]);
      setRun(r);
    } catch (e) { setError(e.message); } finally { setCreating(false); }
  };

  const onEvent = async (stageId, event) => {
    const updated = await postStageEvent(run.id, stageId, event);
    setRun(updated);
  };

  const onExecute = async (stageId, action) => {
    await executeStage(run.id, stageId, action);
    // Pull the run so the new 'started'/job snapshot renders; the polling
    // effect below then streams logs + the terminal state.
    setRun(await fetchRun(run.id));
  };

  // While any stage has a running console job, poll the run so streamed logs
  // + the terminal state (complete/awaiting_human/failed) land automatically.
  // KNOWN COST (PR #69 review, deferred): each fetchRun re-serializes the run's
  // FULL event log, incl. every log chunk. Fine at pilot scale; before heavy/
  // automated use, move the log tail to a since-cursor / tail endpoint rather
  // than the run GET. Tracked in the build plan's Track B deferrals.
  const anyJobRunning = !!run && run.stages.some((s) => s.job && s.job.state === 'running');
  const runIdForPoll = run && run.id;
  useEffect(() => {
    if (!anyJobRunning || !runIdForPoll) return undefined;
    const id = setInterval(async () => {
      try { setRun(await fetchRun(runIdForPoll)); } catch (e) { setError(e.message); }
    }, 1500);
    return () => clearInterval(id);
  }, [anyJobRunning, runIdForPoll]);

  if (loading) return <div className="text-gray-400 text-sm">Loading onboarding runs…</div>;

  return (
    <div className="bg-white rounded-lg border p-4">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h3 className="text-sm font-semibold text-gray-700">
            Pipeline run {run ? `#${run.id}` : ''}
          </h3>
          <p className="text-xs text-gray-400">
            Process state (stage graph S0-S9). The checklist above is the DB-live truth;
            this panel tracks who did what, when, with which artifacts.
          </p>
        </div>
        {!run && !runId && (
          <button onClick={onCreate} disabled={creating}
            className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">
            {creating ? 'Starting…' : 'Start onboarding run'}
          </button>
        )}
      </div>
      {error && <div className="text-red-600 text-xs mb-2">{error}</div>}
      {run && (
        <>
          <div className="text-xs text-gray-500 mb-2">
            created by <span className="font-mono">{run.created_by}</span> ·
            budget: {run.budget_usd === null ? 'none (no console LLM spend)' : `$${run.budget_usd}`} ·
            decisions: {run.decisions ? `rev ${run.decisions.revision}` : 'not recorded (S1)'}
          </div>
          <div>
            {run.stages.map((s) => (
              <StageRow key={s.id} run={run} stage={s}
                onEvent={onEvent} onExecute={onExecute} onError={setError} />
            ))}
          </div>
        </>
      )}
      {!run && runs.length === 0 && (
        <div className="text-sm text-gray-400">No pipeline runs for this institution yet.</div>
      )}
    </div>
  );
}

export default RunPanel;
